import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { db, auth } from '../firebaseConfig';
import { collection, query, orderBy, where, getDocs, addDoc, serverTimestamp, limit, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import emailToName from '../utils/emailToName';
import { useNavigate } from 'react-router-dom';
import Holidays from 'date-holidays';
import cachedApi from '../utils/cachedApi';
import { socket } from '../socket';

const hd = new Holidays('KR');

function getHolidayName(date: Date): string | null {
  const h = hd.isHoliday(date);
  if (h && h.length > 0) return h[0].name;
  return null;
}
function isHolidayOrSunday(date: Date): boolean {
  return !!getHolidayName(date) || date.getDay() === 0;
}

function formatDate(date: Date) {
  // toISOString()은 UTC 기준이므로, 로컬 날짜로 변환
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// 일정 등록 모달
const ScheduleRegisterModal = ({ open, onClose, onRegister, defaultDate }: { open: boolean, onClose: () => void, onRegister: (title: string, content: string, color: string, date: string) => void, defaultDate: string }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedColor, setSelectedColor] = useState('#d32f2f');
  const [loading, setLoading] = useState(false);
  
  // 색상 옵션들
  const colorOptions = [
    { name: '빨간색', value: '#d32f2f', bg: '#fff5f5', border: '#fed7d7' },
    { name: '파란색', value: '#1976d2', bg: '#f0f8ff', border: '#b3d9ff' },
    { name: '초록색', value: '#2e7d32', bg: '#f0fff4', border: '#9ae6b4' },
    { name: '보라색', value: '#7b1fa2', bg: '#faf5ff', border: '#d6bcfa' },
    { name: '주황색', value: '#ed6c02', bg: '#fffaf0', border: '#fbd38d' },
    { name: '회색', value: '#666666', bg: '#f8f9fa', border: '#e2e8f0' }
  ];
  
  useEffect(() => { 
    setTitle(''); 
    setSelectedColor('#d32f2f');
  }, [defaultDate]);
  
  const handleSubmit = async () => {
    if (!title.trim()) return;
    setLoading(true);
    // 제목은 MM-DD + 사용자입력
    const mmdd = defaultDate.slice(5, 10);
    await onRegister(`${mmdd} ${title}`, content, selectedColor, defaultDate);
    setLoading(false);
    setTitle(''); setContent('');
    setSelectedColor('#d32f2f');
    onClose();
  };
  if (!open) return null;
  const mmdd = defaultDate.slice(5, 10);
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 10000 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 340, maxWidth: 480, minHeight: 220, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '20%', transform: 'translate(-50%, 0)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 18, color: '#1976d2' }}>일정 등록</div>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'red', minWidth: 54 }}>{mmdd}</span>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="제목" style={{ flex: 1, fontSize: 16, padding: 8, borderRadius: 4, border: '1px solid #bbb', marginBottom: 0 }} />
        </div>
        <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="내용" style={{ width: '100%', fontSize: 15, padding: 8, borderRadius: 4, border: '1px solid #bbb', minHeight: 60, marginBottom: 12 }} />
        
        {/* 색상 선택 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#444', marginBottom: 8 }}>일정 색상 선택:</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {colorOptions.map((color) => (
              <button
                key={color.value}
                onClick={() => setSelectedColor(color.value)}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: selectedColor === color.value ? '3px solid #333' : '2px solid #ddd',
                  backgroundColor: color.bg,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                title={color.name}
              >
                <div style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: color.value,
                  border: '1px solid #fff'
                }} />
              </button>
            ))}
          </div>
        </div>
        
        <button style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 22px', fontWeight: 600, fontSize: 16, marginTop: 8, cursor: 'pointer' }} onClick={handleSubmit} disabled={loading}>{loading ? '등록중...' : '등록'}</button>
      </div>
    </div>
  );
};

// 일정 수정 모달
const ScheduleEditModal = ({ open, onClose, schedule, onSave }: { open: boolean, onClose: () => void, schedule: any, onSave: (title: string, content: string, color: string, date: string) => void }) => {
  const [title, setTitle] = useState(schedule?.title || '');
  const [content, setContent] = useState(schedule?.content || '');
  const [selectedColor, setSelectedColor] = useState(schedule?.color || '#d32f2f');
  const [loading, setLoading] = useState(false);
  
  // 색상 옵션들
  const colorOptions = [
    { name: '빨간색', value: '#d32f2f', bg: '#fff5f5', border: '#fed7d7' },
    { name: '파란색', value: '#1976d2', bg: '#f0f8ff', border: '#b3d9ff' },
    { name: '초록색', value: '#2e7d32', bg: '#f0fff4', border: '#9ae6b4' },
    { name: '보라색', value: '#7b1fa2', bg: '#faf5ff', border: '#d6bcfa' },
    { name: '주황색', value: '#ed6c02', bg: '#fffaf0', border: '#fbd38d' },
    { name: '회색', value: '#666666', bg: '#f8f9fa', border: '#e2e8f0' }
  ];
  
  useEffect(() => {
    setTitle(schedule?.title || '');
    setContent(schedule?.content || '');
    setSelectedColor(schedule?.color || '#d32f2f');
  }, [schedule]);
  
  const handleSubmit = async () => {
    if (!title.trim()) return;
    setLoading(true);
    await onSave(title, content, selectedColor, schedule?.date);
    setLoading(false);
    onClose();
  };
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 10001 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 340, maxWidth: 480, minHeight: 220, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '20%', transform: 'translate(-50%, 0)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 18, color: '#1976d2' }}>일정 수정</div>
        <div style={{ marginBottom: 12 }}>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="제목" style={{ width: '100%', fontSize: 16, padding: 8, borderRadius: 4, border: '1px solid #bbb', marginBottom: 8 }} />
          <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="내용" style={{ width: '100%', fontSize: 15, padding: 8, borderRadius: 4, border: '1px solid #bbb', minHeight: 60, marginBottom: 12 }} />
        </div>
        
        {/* 색상 선택 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#444', marginBottom: 8 }}>일정 색상 선택:</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {colorOptions.map((color) => (
              <button
                key={color.value}
                onClick={() => setSelectedColor(color.value)}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: selectedColor === color.value ? '3px solid #333' : '2px solid #ddd',
                  backgroundColor: color.bg,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                title={color.name}
              >
                <div style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: color.value,
                  border: '1px solid #fff'
                }} />
              </button>
            ))}
          </div>
        </div>
        
        <button style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 22px', fontWeight: 600, fontSize: 16, marginTop: 8, cursor: 'pointer' }} onClick={handleSubmit} disabled={loading}>{loading ? '저장중...' : '저장'}</button>
      </div>
    </div>
  );
};

// 일정 전체보기 모달
const ScheduleListModal = ({ open, onClose, schedules, onEdit, onDelete }: { open: boolean, onClose: () => void, schedules: any[], onEdit: (schedule: any) => void, onDelete: (id: string) => void }) => {
  const user = auth.currentUser;
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 9999 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 600, maxWidth: 900, minHeight: 500, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '10%', transform: 'translate(-50%, 0)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 22, marginBottom: 18, color: '#1976d2' }}>전체 일정</div>
        <div style={{ maxHeight: 600, overflowY: 'auto' }}>
          {schedules.length === 0 ? (
            <div style={{ color: '#888', textAlign: 'center', marginTop: 40 }}>등록된 일정이 없습니다.</div>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%', marginBottom: 16, minHeight: 210, maxHeight: 400, overflowY: 'auto' }}>
              {schedules.map(s => {
                const isMine = user && s.authorId === user.uid;
                const mmddMatch = s.title.match(/^\(\d{2}-\d{2}\)/);
                const mmdd = mmddMatch ? mmddMatch[1] : '';
                const titleRest = mmdd ? s.title.slice(mmdd.length).trim() : s.title;
                return (
                  <li key={s.id} style={{ borderBottom: '1px solid #eee', padding: '12px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2 }}>
                    {mmdd && <span style={{ color: '#444', marginRight: 4 }}>{mmdd}</span>}
                    <span style={{ color: s.color || '#d32f2f' }}>{titleRest}</span>
                  </div>
                      <div style={{ color: '#444', fontSize: 14, margin: '4px 0 2px 0', whiteSpace: 'pre-line' }}>{s.content}</div>
                      <div style={{ marginTop: 2 }}>
                        <span style={{ color: '#222', fontSize: 14, fontWeight: 700 }}>{emailToName[s.authorEmail] || s.authorEmail}</span>
                      </div>
                    </div>
                    {isMine && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <button style={{ background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }} onClick={() => onEdit(s)}>수정</button>
                        <button style={{ background: '#fff', color: '#d32f2f', border: '1px solid #d32f2f', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }} onClick={() => onDelete(s.id)}>삭제</button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

// 일정 팝업 모달
const SchedulePopupModal = ({ open, onClose, todaySchedules, tomorrowSchedules, onDontShowToday }: { 
  open: boolean, 
  onClose: () => void, 
  todaySchedules: any[], 
  tomorrowSchedules: any[], 
  onDontShowToday: () => void 
}) => {
  const [dontShowToday, setDontShowToday] = useState(false);
  
  const handleClose = () => {
    if (dontShowToday) {
      onDontShowToday();
    }
    onClose();
  };
  
  if (!open) return null;
  
  return (
    <div style={{ 
      position: 'fixed', 
      left: 0, 
      top: 0, 
      width: '100vw', 
      height: '100vh', 
      background: 'rgba(0,0,0,0.5)', 
      zIndex: 10002,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ 
        background: '#fff', 
        borderRadius: 16, 
        minWidth: 500, 
        maxWidth: 600, 
        boxShadow: '0 4px 24px rgba(0,0,0,0.2)', 
        padding: 32, 
        position: 'relative',
        maxHeight: '80vh',
        overflow: 'auto'
      }}>
        {/* 닫기 버튼 */}
        <button 
          onClick={handleClose} 
          style={{ 
            position: 'absolute', 
            top: 16, 
            right: 16, 
            fontSize: 24, 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer',
            color: '#666'
          }}
        >
          ×
        </button>
        
        {/* 제목 */}
        <div style={{ 
          fontWeight: 700, 
          fontSize: 17, 
          marginBottom: 24, 
          color: '#666666',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3H5C3.89 3 3 3.89 3 5V19C3 20.11 3.89 21 5 21H19C20.11 21 21 20.11 21 19V5C21 3.89 20.11 3 19 3ZM19 19H5V8H19V19Z" fill="#666666"/>
            <path d="M7 5H9V7H7V5ZM11 5H13V7H11V5ZM15 5H17V7H15V5Z" fill="#666666"/>
          </svg>
          오늘 & 내일 일정
        </div>
        
        {/* 오늘 일정 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ 
            fontWeight: 700, 
            fontSize: 18, 
            marginBottom: 12, 
            color: '#d32f2f',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <span>오늘</span>
            <span style={{ fontSize: 14, color: '#666' }}>
              ({new Date().toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' })})
            </span>
          </div>
          
          {todaySchedules.length === 0 ? (
            <div style={{ 
              color: '#888', 
              textAlign: 'center', 
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: 8,
              fontSize: 14
            }}>
              오늘 예정된 일정이 없습니다.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {todaySchedules.map((s, idx) => {
                const mmddMatch = s.title.match(/^(\d{2}-\d{2})/);
                const mmdd = mmddMatch ? mmddMatch[1] : '';
                const titleRest = mmdd ? s.title.slice(mmdd.length).trim() : s.title;
                
                return (
                  <div key={idx} style={{ 
                    padding: '12px 16px', 
                    backgroundColor: '#fff5f5', 
                    borderRadius: 8, 
                    border: '1px solid #fed7d7',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12
                  }}>
                    <div style={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: s.color || '#d32f2f',
                      flexShrink: 0
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: 600, 
                        fontSize: 15, 
                        color: s.color || '#d32f2f',
                        marginBottom: 2
                      }}>
                        {titleRest}
                      </div>
                      <div style={{ 
                        color: '#666', 
                        fontSize: 13 
                      }}>
                        ({emailToName[s.authorEmail] || s.authorEmail})
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* 내일 일정 */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ 
            fontWeight: 700, 
            fontSize: 18, 
            marginBottom: 12, 
            color: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
            <span>내일</span>
            <span style={{ fontSize: 14, color: '#666' }}>
              ({new Date(Date.now() + 24 * 60 * 60 * 1000).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'long' })})
            </span>
          </div>
          
          {tomorrowSchedules.length === 0 ? (
            <div style={{ 
              color: '#888', 
              textAlign: 'center', 
              padding: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: 8,
              fontSize: 14
            }}>
              내일 예정된 일정이 없습니다.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tomorrowSchedules.map((s, idx) => {
                const mmddMatch = s.title.match(/^(\d{2}-\d{2})/);
                const mmdd = mmddMatch ? mmddMatch[1] : '';
                const titleRest = mmdd ? s.title.slice(mmdd.length).trim() : s.title;
                
                return (
                  <div key={idx} style={{ 
                    padding: '12px 16px', 
                    backgroundColor: '#f0f8ff', 
                    borderRadius: 8, 
                    border: '1px solid #b3d9ff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12
                  }}>
                    <div style={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: s.color || '#1976d2',
                      flexShrink: 0
                    }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: 600, 
                        fontSize: 15, 
                        color: s.color || '#1976d2',
                        marginBottom: 2
                      }}>
                        {titleRest}
                      </div>
                      <div style={{ 
                        color: '#666', 
                        fontSize: 13 
                      }}>
                        ({emailToName[s.authorEmail] || s.authorEmail})
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* 하단 체크박스 */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 8, 
          marginBottom: 16,
          padding: '12px 16px',
          backgroundColor: '#f8f9fa',
          borderRadius: 8
        }}>
          <input 
            type="checkbox" 
            id="dontShowToday" 
            checked={dontShowToday} 
            onChange={(e) => setDontShowToday(e.target.checked)}
            style={{ width: 16, height: 16 }}
          />
          <label 
            htmlFor="dontShowToday" 
            style={{ 
              fontSize: 14, 
              color: '#666', 
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            오늘은 더 이상 보지 않기
          </label>
        </div>
        
        {/* 확인 버튼 */}
        <button 
          onClick={handleClose}
          style={{ 
            background: '#1976d2', 
            color: '#fff', 
            border: 'none', 
            borderRadius: 8, 
            padding: '12px 24px', 
            fontWeight: 600, 
            fontSize: 16, 
            cursor: 'pointer',
            width: '100%'
          }}
        >
          확인
        </button>
      </div>
    </div>
  );
};

const ScheduleWidget = () => {
  const [value, setValue] = useState<Date | null>(new Date());
  const [schedules, setSchedules] = useState<any[]>([]);
  const [showRegister, setShowRegister] = useState(false);
  const [registerDate, setRegisterDate] = useState(formatDate(new Date()));
  const [showEdit, setShowEdit] = useState(false);
  const [editSchedule, setEditSchedule] = useState<any>(null);
  const [showAll, setShowAll] = useState(false);
  const [showSchedulePopup, setShowSchedulePopup] = useState(false);
  const navigate = useNavigate();

  // 일정 불러오기 (전체)
  const fetchSchedules = async () => {
    try {
      // 캐시된 API 사용
      // const data = await cachedApi.getSchedules();
      // setSchedules(data);
      // ↓ 항상 Firestore에서 직접 조회
      const q = query(collection(db, 'schedules'), orderBy('date', 'desc'), orderBy('createdAt', 'desc'), limit(30));
      const snap = await getDocs(q);
      setSchedules(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('일정 조회 실패:', error);
      setSchedules([]);
    }
  };
  useEffect(() => { 
    fetchSchedules(); 
    
    // 일정 팝업 표시 여부 확인
    const checkSchedulePopup = () => {
      const today = new Date().toDateString();
      const dontShowDate = localStorage.getItem('schedulePopupDontShow');
      
      // 오늘 날짜와 저장된 날짜가 다르면 팝업 표시
      if (dontShowDate !== today) {
        setShowSchedulePopup(true);
      }
    };
    
    // 일정 로딩 후 팝업 체크
    const timer = setTimeout(checkSchedulePopup, 1000);
    return () => clearTimeout(timer);
  }, []);

  // 날짜별 일정
  const schedulesForDate = (date: Date) => {
    const d = formatDate(date);
    return schedules.filter(s => s.date === d);
  };
  
  // 오늘 일정
  const todaySchedules = schedulesForDate(new Date());
  
  // 내일 일정
  const tomorrowSchedules = schedulesForDate(new Date(Date.now() + 24 * 60 * 60 * 1000));
  
  // 팝업 닫기 처리
  const handleDontShowToday = () => {
    const today = new Date().toDateString();
    localStorage.setItem('schedulePopupDontShow', today);
  };

  // 일정 등록
  const handleRegister = async (title: string, content: string, color: string, date: string) => {
    const user = auth.currentUser;
    const docRef = await addDoc(collection(db, 'schedules'), {
      title,
      content,
      color: color || '#d32f2f',
      date,
      createdAt: serverTimestamp(),
      authorId: user?.uid || '',
      authorEmail: user?.email || '',
    });
    
    // 소켓 이벤트 발생
    socket.emit('schedule-added', {
      id: docRef.id,
      title,
      content,
      color: color || '#d32f2f',
      date,
      authorEmail: user?.email || ''
    });
    
    await fetchSchedules();
  };

  // 달력 날짜 클릭 시 일정 표시/등록
  const handleDayClick = (date: Date) => {
    setRegisterDate(formatDate(date));
    setShowRegister(true);
  };

  // 달력 타일에 일정 있는 날짜 dot 표시
  const tileContent = ({ date, view }: { date: Date, view: string }) => {
    if (view === 'month') {
      const holidayName = getHolidayName(date);
      const daySchedules = schedulesForDate(date);
      return (
        <div style={{ marginTop: 2, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {holidayName && (
            <span style={{ color: '#d32f2f', fontSize: 13, fontWeight: 700 }}>{holidayName}</span>
          )}
          {daySchedules && daySchedules.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {daySchedules.slice(0, 2).map((s, idx) => {
                const titleWithoutDate = s.title.replace(/^(\d{2}-\d{2})\s*/, '');
                const scheduleColor = s.color || '#d32f2f';
                
                // 텍스트를 앞에서부터 자르기 (최대 10글자)
                const maxLength = 10;
                const displayText = titleWithoutDate.length > maxLength 
                  ? titleWithoutDate.substring(0, maxLength) + '...' 
                  : titleWithoutDate;
                
                return (
                  <li key={idx} style={{ 
                    fontSize: 12, 
                    color: scheduleColor, 
                    fontWeight: 600, 
                    whiteSpace: 'nowrap', 
                    overflow: 'hidden', 
                    maxWidth: 120,
                    textAlign: 'left',
                    textOverflow: 'clip'
                  }}>
                    {displayText}
                  </li>
                );
              })}
              {daySchedules.length > 2 && (
                <li style={{ fontSize: 11, color: '#888' }}>+{daySchedules.length - 2}개</li>
              )}
            </ul>
          )}
        </div>
      );
    }
    return null;
  };

  const tileClassName = ({ date, view }: { date: Date, view: string }) => {
    if (view === 'month' && isHolidayOrSunday(date)) {
      return 'calendar-holiday';
    }
    return '';
  };

  // 최근 일정 5개 (공지사항 스타일)
  const sortedSchedules = [...schedules].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const recentSchedules = sortedSchedules.slice(0, 5);

  // 일정 수정
  const handleEdit = (schedule: any) => {
    setEditSchedule(schedule);
    setShowEdit(true);
  };
  const handleEditSave = async (title: string, content: string, color: string, date: string) => {
    if (!editSchedule) return;
    await updateDoc(doc(db, 'schedules', editSchedule.id), {
      title,
      content,
      color: color || '#d32f2f',
      date,
    });
    
    // 소켓 이벤트 발생
    socket.emit('schedule-updated', {
      id: editSchedule.id,
      title,
      content,
      color: color || '#d32f2f',
      date,
      authorEmail: auth.currentUser?.email || ''
    });
    
    setShowEdit(false);
    setEditSchedule(null);
    await fetchSchedules();
  };

  // 일정 삭제
  const handleDelete = async (id: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await deleteDoc(doc(db, 'schedules', id));
    
    // 소켓 이벤트 발생
    socket.emit('schedule-deleted', {
      id,
      authorEmail: auth.currentUser?.email || ''
    });
    
    await fetchSchedules();
  };

  const user = auth.currentUser;

  return (
    <>
      <style>{`
        /* 달력 전용 스타일 - 글로벌 CSS 영향 방지 */
        .schedule-widget-calendar .react-calendar {
          width: 100% !important;
          max-width: 100%;
          background: #fff;
          border: none;
          font-family: inherit;
        }
        .schedule-widget-calendar .react-calendar__navigation {
          margin-bottom: 1rem;
        }
        .schedule-widget-calendar .react-calendar__navigation button {
          min-width: 44px;
          background: none;
          font-size: 16px;
          margin-top: 8px;
          color: #1976d2;
          font-weight: 600;
        }
        .schedule-widget-calendar .react-calendar__navigation button:enabled:hover,
        .schedule-widget-calendar .react-calendar__navigation button:enabled:focus {
          background-color: #e6f3ff;
          border-radius: 6px;
        }
        .schedule-widget-calendar .react-calendar__navigation button[disabled] {
          background-color: #f0f0f0;
          color: #666;
        }
        .schedule-widget-calendar .react-calendar__month-view__weekdays {
          text-align: center;
          text-transform: uppercase;
          font-weight: bold;
          font-size: 0.75em;
          color: #666;
          display: grid !important;
          grid-template-columns: repeat(7, 1fr) !important;
        }
        .schedule-widget-calendar .react-calendar__month-view__weekdays__weekday {
          padding: 0.5em;
        }
        .schedule-widget-calendar .react-calendar__month-view__days {
          display: grid !important;
          grid-template-columns: repeat(7, 1fr) !important;
          gap: 1px;
        }
        .schedule-widget-calendar .react-calendar__month-view__weekdays__weekday abbr {
          text-decoration: none;
          font-size: 14px;
          font-weight: 600;
          color: #666;
        }
        .schedule-widget-calendar .react-calendar__month-view__days__day {
          min-width: 70px !important;
          padding: 0.75em 0.5em;
          background: none;
          text-align: center;
          line-height: 16px;
          font-size: 14px;
          color: #333;
          font-weight: 500;
          min-height: 60px;
          margin: 1px;
        }
        .schedule-widget-calendar .react-calendar__month-view__days__day--neighboringMonth {
          color: #ccc;
        }
        .schedule-widget-calendar .react-calendar__tile {
          max-width: 100%;
          min-width: 70px;
          padding: 8px 8px;
          background: none;
          text-align: center;
          line-height: 16px;
          font-size: 14px;
          color: #333;
          font-weight: 500;
          border: none;
          border-radius: 6px;
          min-height: 50px;
          margin: 2px;
        }
        .schedule-widget-calendar .react-calendar__tile:enabled:hover,
        .schedule-widget-calendar .react-calendar__tile:enabled:focus {
          background-color: #e6f3ff;
          color: #1976d2;
        }
        .schedule-widget-calendar .react-calendar__tile--now {
          background: #ffff76;
          color: #333;
        }
        .schedule-widget-calendar .react-calendar__tile--now:enabled:hover,
        .schedule-widget-calendar .react-calendar__tile--now:enabled:focus {
          background: #ffffa9;
        }
        .schedule-widget-calendar .react-calendar__tile--hasActive {
          background: #e6f3ff;
          color: #1976d2;
        }
        .schedule-widget-calendar .react-calendar__tile--hasActive:enabled:hover,
        .schedule-widget-calendar .react-calendar__tile--hasActive:enabled:focus {
          background: #d1e7ff;
        }
        .schedule-widget-calendar .react-calendar__tile--active {
          background: #e6f3ff;
          color: #1976d2;
        }
        .schedule-widget-calendar .react-calendar__tile--active:enabled:hover,
        .schedule-widget-calendar .react-calendar__tile--active:enabled:focus {
          background: #d1e7ff;
        }
        .schedule-widget-calendar .react-calendar--selectRange .react-calendar__tile--hover {
          background-color: #e6e6e6;
        }
        .schedule-widget-calendar .react-calendar__tile abbr {
          font-size: 14px !important;
          font-weight: 500 !important;
          color: inherit !important;
          text-decoration: none !important;
          line-height: 16px !important;
        }
        .schedule-widget-calendar .react-calendar__tile button {
          background: none !important;
          border: none !important;
          padding: 0 !important;
          margin: 0 !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          color: inherit !important;
          line-height: 16px !important;
          width: 100% !important;
          height: auto !important;
        }
        .calendar-holiday {
          color: #d32f2f !important;
          font-weight: 700;
        }
      `}</style>
      <div style={{ display: 'flex', flexDirection: 'row', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap', width: '100%' }}>
        {/* 일정 목록 카드 */}
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 16px rgba(0,0,0,0.10)', minWidth: 260, maxWidth: 420, flex: '1 1 280px', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minHeight: 300 }}>
        <div style={{ fontWeight: 700, fontSize: 22, color: '#444444', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>일정</span>
          <button style={{ background: '#eee', color: '#1976d2', border: 'none', borderRadius: 6, padding: '7px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }} onClick={() => navigate('/schedule-board')}>전체보기</button>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%', marginBottom: 16, minHeight: 280, maxHeight: 320, overflow: 'hidden' }}>
          {recentSchedules.map(s => {
            const isMine = user && s.authorId === user.uid;
            // MM-DD 추출
            const mmddMatch = s.title.match(/^(\d{2}-\d{2})/);
            const mmdd = mmddMatch ? mmddMatch[1] : '';
            const titleRest = mmdd ? s.title.slice(mmdd.length).trim() : s.title;
            return (
              <li key={s.id} style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ flex: 1 }}>
                                                          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div>
                      {mmdd && <span style={{ color: '#444', marginRight: 4 }}>{mmdd}</span>}
                      <span style={{ color: s.color || '#d32f2f' }}>{titleRest}</span>
                    </div>
                    <span style={{ color: '#666', fontSize: 13, fontWeight: 500 }}>({emailToName[s.authorEmail] || s.authorEmail})</span>
                  </div>
                  <div style={{ color: '#444', fontSize: 14, marginBottom: 2, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.4, height: '1.4em' }}>{s.content}</div>
                </div>
                {isMine && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <button style={{ background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }} onClick={() => handleEdit(s)}>수정</button>
                    <button style={{ background: '#fff', color: '#d32f2f', border: '1px solid #d32f2f', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }} onClick={() => handleDelete(s.id)}>삭제</button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      {/* 달력 카드 */}
      <div className="schedule-widget-calendar" style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 16px rgba(0,0,0,0.10)', flex: '2 1 400px', minWidth: 280, maxWidth: '100%', width: '100%', padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: 300 }}>
        <Calendar
          onChange={v => setValue(v as Date)}
          value={value}
          onClickDay={handleDayClick}
          tileContent={tileContent}
          tileClassName={tileClassName}
          calendarType="gregory"
          locale="ko-KR"
        />
      </div>
      <ScheduleRegisterModal open={showRegister} onClose={() => setShowRegister(false)} onRegister={handleRegister} defaultDate={registerDate} />
      <ScheduleEditModal open={showEdit} onClose={() => { setShowEdit(false); setEditSchedule(null); }} schedule={editSchedule} onSave={handleEditSave} />
      <ScheduleListModal open={showAll} onClose={() => setShowAll(false)} schedules={schedules} onEdit={handleEdit} onDelete={handleDelete} />
      <SchedulePopupModal 
        open={showSchedulePopup} 
        onClose={() => setShowSchedulePopup(false)} 
        todaySchedules={todaySchedules}
        tomorrowSchedules={tomorrowSchedules}
        onDontShowToday={handleDontShowToday}
      />
    </div>
  </>
);
};  
export default ScheduleWidget; 
